/**
 * Data Transformers - Convert Strapi API responses to widget format
 */

/**
 * Transforma resposta Strapi v4 (com attributes) para formato simplificado
 */
export function transformStrapiV4(data) {
  if (!data) return null;

  // Se for array
  if (Array.isArray(data)) {
    return data.map(item => transformStrapiV4(item));
  }

  // Se tiver estrutura data/attributes
  if (data.data) {
    if (Array.isArray(data.data)) {
      return data.data.map(item => transformSingleItem(item));
    }
    return transformSingleItem(data.data);
  }

  return transformSingleItem(data);
}

/**
 * Transforma um único item
 */
function transformSingleItem(item) {
  if (!item) return null;

  // Suporta Strapi v4 (com attributes) e v5 (sem attributes)
  const transformed = item.attributes
    ? { id: item.id, ...item.attributes }
    : { ...item };

  // Transforma relacionamentos aninhados
  Object.keys(transformed).forEach(key => {
    if (transformed[key] && typeof transformed[key] === 'object') {
      if (transformed[key].data) {
        transformed[key] = transformStrapiV4(transformed[key].data);
      }
    }
  });

  return transformed;
}

/**
 * Transforma dados de Banner do Strapi
 */
export function transformBanner(data) {
  const banners = transformStrapiV4(data);

  if (!Array.isArray(banners)) {
    return transformSingleBanner(banners);
  }

  return banners.map(banner => transformSingleBanner(banner));
}

function transformSingleBanner(banner) {
  if (!banner) return null;

  return {
    id: banner.id,
    title: banner.title || banner.Title || '',
    description: banner.description || banner.Description || '',
    image: getImageUrl(banner.image || banner.Image),
    link: banner.link || banner.Link || '#',
    order: banner.order || banner.Order || 0,
    active: banner.active !== undefined ? banner.active : (banner.Active !== undefined ? banner.Active : true)
  };
}

/**
 * Extrai URL da imagem do formato Strapi
 */
export function getImageUrl(imageData, size = 'large') {
  if (!imageData) return '';

  // Se já é string, retorna
  if (typeof imageData === 'string') {
    return imageData;
  }

  // Strapi v4 format
  if (imageData.formats && imageData.formats[size]) {
    return imageData.formats[size].url;
  }

  // URL direta
  if (imageData.url) {
    return imageData.url;
  }

  return '';
}

/**
 * Transforma dados de Artigo do Strapi
 */
export function transformArticle(data) {
  const articles = transformStrapiV4(data);

  if (!Array.isArray(articles)) {
    return transformSingleArticle(articles);
  }

  return articles.map(article => transformSingleArticle(article));
}

function transformSingleArticle(article) {
  if (!article) return null;

  return {
    id: article.id,
    title: article.title || '',
    content: article.content || article.body || '',
    excerpt: article.excerpt || article.summary || '',
    slug: article.slug || '',
    author: article.author || null,
    publishedAt: article.publishedAt || article.published_at || null,
    cover: getImageUrl(article.cover || article.coverImage),
    category: article.category || null,
    tags: article.tags || []
  };
}

/**
 * Transforma dados de Categoria
 */
export function transformCategory(data) {
  const categories = transformStrapiV4(data);

  if (!Array.isArray(categories)) {
    return transformSingleCategory(categories);
  }

  return categories.map(category => transformSingleCategory(category));
}

function transformSingleCategory(category) {
  if (!category) return null;

  return {
    id: category.id,
    name: category.name || '',
    slug: category.slug || '',
    description: category.description || '',
    parent: category.parent || null
  };
}

/**
 * Transforma resposta paginada do Strapi
 */
export function transformPaginated(data) {
  if (!data) return null;

  return {
    data: transformStrapiV4(data.data),
    pagination: {
      page: data.meta?.pagination?.page || 1,
      pageSize: data.meta?.pagination?.pageSize || 25,
      pageCount: data.meta?.pagination?.pageCount || 1,
      total: data.meta?.pagination?.total || 0
    }
  };
}

/**
 * Transforma erros do Strapi para formato padronizado
 */
export function transformError(error) {
  if (!error) return null;

  // Erro do Strapi v4
  if (error.error) {
    return {
      status: error.error.status || 500,
      name: error.error.name || 'Error',
      message: error.error.message || 'Unknown error',
      details: error.error.details || null
    };
  }

  // Erro genérico
  return {
    status: 500,
    name: 'Error',
    message: error.message || 'Unknown error',
    details: error
  };
}

/**
 * Aplica transformação baseada no tipo de conteúdo
 */
export function transformByContentType(contentType, data) {
  const transformers = {
    'banner': transformBanner,
    'banners': transformBanner,
    'article': transformArticle,
    'articles': transformArticle,
    'category': transformCategory,
    'categories': transformCategory
  };

  const transformer = transformers[contentType.toLowerCase()];

  if (transformer) {
    return transformer(data);
  }

  // Fallback para transformação genérica
  return transformStrapiV4(data);
}

/**
 * Valida se dados tem estrutura esperada
 */
export function validateStrapiResponse(data) {
  if (!data) {
    return { valid: false, error: 'Empty response' };
  }

  // Verifica estrutura Strapi v4
  if (data.data !== undefined) {
    return { valid: true };
  }

  // Resposta direta (array ou objeto)
  if (Array.isArray(data) || typeof data === 'object') {
    return { valid: true };
  }

  return { valid: false, error: 'Invalid response structure' };
}
